// useSiretAutoComplete.js
// Hook + composant réutilisable pour la validation/auto-complétion SIRET
// temps réel via l'API `/api/siret/validate/{siret}` (fallback gratuit
// recherche-entreprises.api.gouv.fr).
//
// Usage typique :
//   const { siret, setSiret, lookup, fetchedCompany } = useSiretAutoComplete({
//     initialSiret: form.siret,
//     onAutoFill: (company) => setForm(p => ({
//       ...p,
//       raison_sociale: p.raison_sociale || company.raison_sociale,
//       ville: p.ville || company.ville,
//       code_postal: p.code_postal || company.code_postal,
//       adresse: p.adresse || company.adresse,
//     })),
//   });
//
//   <SiretInput value={siret} onChange={setSiret} lookup={lookup} />

import { useState, useRef, useCallback, useEffect } from 'react';
import { formatCodeNAF, getCodeNAFLabel } from '../utils/codeNaf';
import axios from 'axios';
import { Loader2, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { Input } from './ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_LOOKUP = { status: 'idle', message: '', company: null };

export function useSiretAutoComplete({ initialSiret = '', onAutoFill, debounceMs = 600 } = {}) {
  const [siret, setSiretValue] = useState(initialSiret || '');
  const [lookup, setLookup] = useState(INITIAL_LOOKUP);
  const timerRef = useRef(null);
  const onAutoFillRef = useRef(onAutoFill);
  useEffect(() => { onAutoFillRef.current = onAutoFill; }, [onAutoFill]);

  const validateNow = useCallback(async (raw) => {
    const cleaned = (raw || '').replace(/[\s-]/g, '');
    if (cleaned.length !== 14 || !/^\d{14}$/.test(cleaned)) {
      setLookup(INITIAL_LOOKUP);
      return;
    }
    setLookup({ status: 'loading', message: 'Vérification…', company: null });
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${BACKEND_URL}/api/siret/validate/${cleaned}`,
        token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
      );
      const d = r.data || {};
      if (!d.format_valid) {
        setLookup({ status: 'invalid', message: d.validation_message || 'SIRET invalide', company: null });
        return;
      }
      const company = d.company_data;
      if (!company) {
        setLookup({ status: 'unknown', message: 'Format valide — entreprise non trouvée', company: null });
        return;
      }
      const closed = company.etat_administratif && company.etat_administratif !== 'A';
      const nafCode = formatCodeNAF(company.code_naf);
      const nafLabel = company.code_naf_label || getCodeNAFLabel(nafCode);
      const nafBadge = nafCode
        ? (nafLabel ? ` · NAF ${nafCode} — ${nafLabel}` : ` · NAF ${nafCode}`)
        : '';
      setLookup({
        status: closed ? 'warning' : 'valid',
        message: closed
          ? `${company.raison_sociale} — ⚠️ Établissement fermé`
          : `${company.raison_sociale}${company.ville ? ' (' + company.ville + ')' : ''}${nafBadge}`,
        company,
      });
      if (onAutoFillRef.current && !closed) {
        onAutoFillRef.current(company);
      }
    } catch (err) {
      setLookup({
        status: 'error',
        message: err.response?.data?.detail || 'Vérification SIRET indisponible',
        company: null,
      });
    }
  }, []);

  const setSiret = useCallback((value) => {
    setSiretValue(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    setLookup(INITIAL_LOOKUP);
    timerRef.current = setTimeout(() => validateNow(value), debounceMs);
  }, [validateNow, debounceMs]);

  return {
    siret,
    setSiret,
    lookup,
    fetchedCompany: lookup.company,
    isValid: lookup.status === 'valid',
    isClosed: lookup.status === 'warning',
    revalidate: () => validateNow(siret),
  };
}

/**
 * Champ SIRET avec feedback visuel intégré (icône + message).
 * À utiliser conjointement avec `useSiretAutoComplete`.
 */
export function SiretInput({
  value,
  onChange,
  lookup,
  placeholder = '14 chiffres — auto-complétion',
  testId = 'siret-input',
  className = '',
  ...rest
}) {
  const borderClass =
    lookup?.status === 'valid' ? 'border-emerald-400' :
    lookup?.status === 'warning' ? 'border-amber-400' :
    lookup?.status === 'invalid' || lookup?.status === 'error' ? 'border-red-400' :
    '';
  const Icon =
    lookup?.status === 'loading' ? Loader2 :
    lookup?.status === 'valid' ? CheckCircle :
    lookup?.status === 'warning' ? AlertTriangle :
    lookup?.status === 'invalid' || lookup?.status === 'error' ? X :
    null;
  const iconColor =
    lookup?.status === 'loading' ? 'text-gray-400' :
    lookup?.status === 'valid' ? 'text-emerald-600' :
    lookup?.status === 'warning' ? 'text-amber-600' :
    'text-red-600';

  return (
    <>
      <div className="relative">
        <Input
          {...rest}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          className={`${borderClass} ${Icon ? 'pr-9' : ''} ${className}`}
        />
        {Icon && (
          <Icon
            className={`absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 ${iconColor} ${
              lookup?.status === 'loading' ? 'animate-spin' : ''
            }`}
          />
        )}
      </div>
      {lookup?.message && (
        <p
          data-testid={`${testId}-message`}
          className={
            'text-xs mt-1 ' + (
              lookup.status === 'valid' ? 'text-emerald-700' :
              lookup.status === 'warning' ? 'text-amber-700' :
              lookup.status === 'invalid' || lookup.status === 'error' ? 'text-red-600' :
              'text-gray-500'
            )
          }
        >
          {lookup.message}
        </p>
      )}
    </>
  );
}
